import React from 'react';
import {
    Responsive,
    SimpleList,
    SimpleShowLayout,
    Datagrid,
    SimpleForm,
    TextField,
    BooleanField,
    TextInput,
    SelectInput,
    DisabledInput,
    required
} from 'admin-on-rest';
import {DependentInput} from 'aor-dependent-input';
import List from '../admin/ListStatic';
import Show from '../admin/Show';
import Create from '../admin/Create';
import Edit from '../admin/Edit';
import ValidatedInputField from '../components/ValidatedInputField';
import AccessTable from '../components/AccessTable';
import ViewButton from '../buttons/ViewButton';
import EditButton from '../buttons/EditButton';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {passwordValidation, passwordChangeValidation} from "../shared/validation";
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/social/person-outline';
import FuseTheme from "../global/theme";

export const fuseSysUserIcon = Icon;

const styles = {
    accessBox: {
        display: 'block',
        padding: '4px 10px 4px 10px',
        border: '1px solid ' + FuseTheme.palette.lightTextColor,
        boxSizing: 'border-box',
    },
    avatar: {backgroundColor: "#6e6e6e", width: 40, height: 40, marginRight: 10, verticalAlign: 'bottom'},
    description: {lineHeight: 1.5},
    row: {display: 'flex', justifyContent: 'space-between'},
    rowField: {flex: '0 1 30%', display: 'inline-block', margin: '0 1em 0 0', verticalAlign: 'top'},
    rowShowField: {display: 'inline-block', width: 'auto', minWidth: 100, margin: '0 1em 0 0', verticalAlign: 'top'},
    fieldSmall: {flex: '0 1 10%', display: 'inline-block', maxWidth: '8.5em', overflow: 'hidden'},
    fieldHeader: {
        display: 'block',
        width: '100%',
        fontSize: '16px',
        fontWeight: 'bold',
        color: FuseTheme.palette.lightTextColor,
        margin: '2em 0 0 0',
    },
    fieldInstructions: {
        display: 'block',
        width: '100%',
        fontSize: '14px',
        fontWeight: 'bold',
        color: FuseTheme.palette.lightTextColor,
        margin: '2em 0 2em 0',
    },
    fieldLabel: {
        display: 'block',
        width: '100%',
        fontSize: '14px',
        color: FuseTheme.palette.lightTextColor,
        margin: '2em 0 0 0',
    },
    fieldDescription: {
        flex: '1 1 40%',
        display: 'inline-block',
        width: 'auto',
        height: '2em',
        fontSize: '12px',
        color: FuseTheme.palette.lightTextColor,
        verticalAlign: 'bottom',
    },
    divider: {
        display: 'block',
        width: '100%',
        height: 1,
        backgroundColor: FuseTheme.palette.primary3Color,
        border: 'none',
        margin: '1em 0 0.5em 0'
    },
    hidden: {display: 'none'},
    break: {display: 'block', width: '100%', height: 0, border: 'none', margin: 0},
    paddingBottom: {paddingBottom: '1em'},
};

// Handle event from access Add
const handleAddAccess = (e, index, value) => {
    //console.log('Add ' + e + ' to access list: ', index, value);
};

// Handle event from access Remove
const handleRemoveAccess = (e) => {
    //console.log('Remove ' + e + ' from access list');
};

const MakeAccessTable = ({record, source, type, singleAccess, showButtons, clearAccess}) => {
    let accessAll = false;
    let accessList = [];
    let optionText = 'name';
    if (type.listName === 'systems') {
        accessAll = record.systems_all;
        accessList = (record.systems && !clearAccess) ? record.systems : [];
    }
    if (type.listName === 'sections') {
        accessAll = record.sections_all;
        accessList = (record.sections && !clearAccess) ? record.sections : [];
    }
    return (
        <AccessTable
            {...record}
            listName={type.listName}
            name={type.name}
            single={type.single}
            accessAll={accessAll}
            accessList={accessList}
            clearAccess={clearAccess}
            onAddUpdate={handleAddAccess}
            onRemoveUpdate={handleRemoveAccess}
            optionText={optionText}
            showAdd={showButtons.add}
            showRemove={showButtons.remove}
            singleAccess={type.singleAccess}/>
    )
};

const validatePassword = [required, passwordValidation];
const validateChangePassword = [passwordChangeValidation];

/*
 * View: List
 */

// Set title for List Sysusers
const ListTitle = <span><Avatar icon={<Icon/>} style={styles.avatar}
                                className="avatar-title"/>System Users</span>;

const ListDescription = () => {
    return (
        <span style={styles.description}>
            <p>A System User (Sysuser) is a user of the Admin. A Sysuser account only has access to a single selected
                System, but may be given full access to CloudSent Admin or Reports. They may be restricted to specific
                sections such as Loyalty or Locations.</p>
            <p>Sysusers cannot create, view, or edit Users, nor create or modify Systems.</p>
        </span>
    );
};

/*
 * List Sysusers table
 */
export const fuseSystemUserList = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescription/>} sort={{field: 'email', order: 'ASC'}}
          perPage={20} filter={{role: 'sys_user'}}>
        <Responsive
            small={
                <SimpleList
                    primaryText={record => record.email}
                    secondaryText={record => record.first_name + '  ' + record.last_name}
                    tertiaryText={record => record.active}
                />
            }
            medium={
                <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                    <TextField source="email" label="Email"/>
                    <TextField source="first_name" label="First Name"/>
                    <TextField source="last_name" label="Last Name"/>
                    <TextField source="systems" label="System"/>
                    <BooleanField source="active" label="Active"/>
                    <ViewButton/>
                    <EditButton/>
                </Datagrid>
            }
        />
    </List>
);

/*
 * View: Show
 */

// Set title for Show Sysuser
const ShowTitle = ({record}) => {
    let firstName, lastName;
    if (record) {
        firstName = (record.sys_first_name) ? record.sys_first_name : record.first_name;
        lastName = (record.sys_last_name) ? record.sys_last_name : record.last_name;
    }

    return <span><Avatar icon={<Icon/>} style={styles.avatar}
                         className="avatar-title"/>{record ? `${firstName} ${lastName}` : 'Sysuser'}</span>;
};

const ShowDescription = () => {
    return (
        <span style={styles.description}>
            <p>Review user details below. To edit this Sysuser, click <strong>Edit</strong> above.</p>
        </span>
    );
};

/*
 * Show Sysuser Table
 */
export const fuseSystemUserShow = (props) => (
    <Show title={<ShowTitle/>} desc={<ShowDescription/>} {...props}>
        <SimpleShowLayout>
            <TextField source="sys_email" label="Email"/>
            <TextField source="sys_first_name" label="First Name"/>
            <TextField source="sys_last_name" label="Last Name"/>
            <BooleanField source="active" label="Active" style={styles.rowShowField} elStyle={styles.fieldSmall}/>
            <BooleanField source="locked" label="Locked" style={styles.rowShowField} elStyle={styles.fieldSmall}/>
            <span style={styles.fieldHeader}>Permissions</span>
            {<MakeAccessTable
                type={{listName: 'systems', name: 'Systems', single: 'System', singleAccess: false}}
                showButtons={{'add': false, 'remove': false}}
            />}
            {<MakeAccessTable
                type={{listName: 'sections', name: 'Sections', single: 'Section', singleAccess: false}}
                showButtons={{'add': false, 'remove': false}}
            />}
        </SimpleShowLayout>
    </Show>
);

/*
 * View: Create form
 */

// Set title for Create Sysuser
const CreateTitle = <span><Avatar icon={<Icon/>} style={styles.avatar}
                                  className="avatar-title"/>Create a Sysuser</span>;

const CreateDescription = () => {
    return (
        <span style={styles.description}>
            <p>A System User (Sysuser) is a user of the Admin. A Sysuser account only has access to a single selected
                System, but may be given full access to CloudSent Admin or Reports. They may be restricted to specific
                sections such as Loyalty or Locations.</p>
            <p>Sysusers cannot create, view, or edit Users, nor create or modify Systems.</p>
            <p>Enter the required fields below.</p>
        </span>
    );
};

// Set defaults for Create form
const defaultCreateValues = {
    active: false,
    locked: false,
    sys_op: false,
    sys_admin: false,
    systems_all: false,
    systems: [],
    sections_all: false,
    sections: [],
    locations_all: true,
    locations: [],
    dbas_all: true,
    dbas: [],
    districts_all: true,
    districts: [],
};

/*
 * Create Sysuser form
 */
export const fuseSystemUserCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm defaultValue={defaultCreateValues} submitOnEnter={false} redirect="list">

            <hr style={{...styles.break, ...styles.paddingBottom}}/>
            <span style={styles.fieldDescription}>
                Enter and verify a unique <strong>Email Address</strong> for this Sysuser.
            </span>
            <ValidatedInputField
                formName="record-form"
                source="sys_email"
                fieldType="email"
                label="Email Address*"
                buttonLabel="Check Availability"
                successMessage="This Email is available."
                errorDuplicateMessage="A User with this Email already exists."
                errorRequiredMessage="A unique Email is required."
                fieldResource="Sysusers"
                isRequired={true}
            />
            <TextInput source="sys_password" label="Password" type="password" validate={validatePassword}/>
            <TextInput source="sys_confirm_password" label="Confirm Password" type="password"
                       validate={validatePassword}/>
            <TextInput source="sys_first_name" label="First Name" validate={[required]} style={styles.rowShowField}/>
            <TextInput source="sys_last_name" label="Last Name" validate={[required]} style={styles.rowShowField}/>
            <hr style={styles.break}/>

            <span style={styles.fieldLabel}>
                Sysuser is Active*
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>Active</em>, this Sysuser account can access its assigned System.
                    <br/>
                    If <em>Inactive</em>, this Sysuser account cannot access any Systems.
                </ReactTooltip>
            </span>
            <SelectInput source="active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Active'},
                             {id: false, name: 'Inactive'},
                         ]}
                         options={{
                             floatingLabelText: '',
                         }}
            />

            <span style={styles.fieldHeader}>Access and Permissions</span>
            <hr style={styles.break}/>
            <span style={styles.fieldLabel}>By default, Sysusers have no access to any Systems, CloudSent Admin or
                Reports, nor any Store Locations, DBAs or Districts.</span>
            <span style={styles.fieldLabel}>A Sysuser can access a single System. Select one System from the list. To
                choose a different System, remove the currently selected System.</span>
            <DisabledInput source="systems_all" style={styles.hidden} elStyle={styles.hidden}/>
            <DependentInput dependsOn="systems_all" value={false}>
                {<MakeAccessTable
                    type={{listName: 'systems', name: 'Systems', single: 'System', singleAccess: true}}
                    showButtons={{'add': true, 'remove': true}}
                    clearAccess={true}
                />}
            </DependentInput>

            <span style={styles.fieldLabel}>
                This Sysuser can be given access to one or more Sections for the selected System. Some Sections allow
                full access to a CloudSent area such as Admin or Reports. Select Sections from the list.
            </span>
            <DisabledInput source="sections_all" style={styles.hidden} elStyle={styles.hidden}/>
            <DependentInput dependsOn="sections_all" value={false}>
                {<MakeAccessTable
                    type={{listName: 'sections', name: 'Sections', single: 'Section', singleAccess: false}}
                    showButtons={{'add': true, 'remove': true}}
                    clearAccess={true}
                />}
            </DependentInput>

            {/* FPO; Locations/DBAs/Districts are set to true */}
            <DependentInput dependsOn="systems_all" value={null}>

                <span style={styles.fieldLabel}>
                Sysuser can access all Locations*
                <TooltipIcon dataFor="ttAllLocations"/>
                <ReactTooltip id="ttAllLocations" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>True</em>, this Sysuser account can access all Store Locations.
                    <br/>
                    If <em>False</em>, this Sysuser account can access specific Store Locations.
                    <br/><br/>
                    Available Locations should be selected from the list if set to false.
                </ReactTooltip>
            </span>
                <SelectInput source="locations_all" validate={[required]} options={{floatingLabelText: ''}}
                             choices={[
                                 {id: true, name: 'True'},
                                 {id: false, name: 'False'},
                             ]}/>

                <span style={styles.fieldLabel}>
                Sysuser can access all DBAs*
                <TooltipIcon dataFor="ttAllDBAs"/>
                <ReactTooltip id="ttAllDBAs" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>True</em>, this Sysuser account can access all DBAs.
                    <br/>
                    If <em>False</em>, this Sysuser account can access specific DBAs.
                    <br/><br/>
                    Available DBAs should be selected from the list if set to false.
                </ReactTooltip>
            </span>
                <SelectInput source="dbas_all" validate={[required]} options={{floatingLabelText: ''}}
                             choices={[
                                 {id: true, name: 'True'},
                                 {id: false, name: 'False'},
                             ]}/>

                <span style={styles.fieldLabel}>
                Sysuser can access all Districts*
                <TooltipIcon dataFor="ttAllDistricts"/>
                <ReactTooltip id="ttAllDistricts" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>True</em>, this Sysuser account can access all Districts.
                    <br/>
                    If <em>False</em>, this Sysuser account can access specific Districts.
                    <br/><br/>
                    Available Districts should be selected from the list if set to false.
                </ReactTooltip>
            </span>
                <SelectInput source="districts_all" validate={[required]} options={{floatingLabelText: ''}}
                             choices={[
                                 {id: true, name: 'True'},
                                 {id: false, name: 'False'},
                             ]}/>
            </DependentInput>

            <span style={styles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Sysusers List.
            </span>
            <hr style={{...styles.break, ...styles.paddingBottom}}/>
        </SimpleForm>
    </Create>
);

/*
 * View: Edit form
 */

// Set title for Edit Sysuser
const EditTitle = ({record}) => {
    let firstName, lastName;
    if (record) {
        firstName = (record.sys_first_name) ? record.sys_first_name : record.first_name;
        lastName = (record.sys_last_name) ? record.sys_last_name : record.last_name;
    }

    return <span><Avatar icon={<Icon/>} style={styles.avatar}
                         className="avatar-title"/>Edit {record ? `${firstName} ${lastName}` : 'Sysuser'}</span>;
};

const EditDescription = () => {
    return (
        <span style={styles.description}>
            <p>Modify fields in the tabs below. Click <strong>Save</strong> to store this Sysuser's details.</p>
        </span>
    );
};

/*
 * Edit Sysuser form
 */
export const fuseSystemUserEdit = (props) => (
    <Edit {...props} title={<EditTitle/>} desc={<EditDescription/>}>
        <SimpleForm submitOnEnter={false}>
            <DisabledInput source="sys_email" label="Email"/>
            <span style={styles.fieldDescription}>
                Email cannot be edited.
                <TooltipIcon dataFor="ttEmail"/>
                <ReactTooltip id="ttEmail" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Email addresses must be unique within the system. A Sysuser's email cannot be edited.
                </ReactTooltip>
            </span>
            <hr style={styles.break}/>
            <TextInput source="sys_first_name" label="First Name" validate={[required]} style={styles.rowShowField}/>
            <TextInput source="sys_last_name" label="Last Name" validate={[required]} style={styles.rowShowField}/>
            <hr style={styles.break}/>

            <span style={styles.fieldLabel}>
                A Sysuser can access a single System. Select one System from the list. To choose a different System,
                remove the currently selected System.
            </span>
            <DisabledInput source="systems_all" style={styles.hidden} elStyle={styles.hidden}/>
            <DependentInput dependsOn="systems_all" value={false}>
                {<MakeAccessTable
                    type={{listName: 'systems', name: 'Systems', single: 'System', singleAccess: true}}
                    showButtons={{'add': true, 'remove': true}}
                />}
            </DependentInput>

            <span style={styles.fieldLabel}>
                This Sysuser can be given access to one or more Sections for the selected System. Some Sections allow
                full access to a CloudSent area such as Admin or Reports. Select Sections from the list.
            </span>
            <DisabledInput source="sections_all" style={styles.hidden} elStyle={styles.hidden}/>
            <DependentInput dependsOn="sections_all" value={false}>
                {<MakeAccessTable
                    type={{listName: 'sections', name: 'Sections', single: 'Section', singleAccess: false}}
                    showButtons={{'add': true, 'remove': true}}
                />}
            </DependentInput>

            <span style={styles.fieldLabel}>
                Sysuser is Active*
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>Active</em>, this Sysuser account can access its assigned System.
                    <br/>
                    If <em>Inactive</em>, this Sysuser account cannot access any Systems.
                </ReactTooltip>
            </span>
            <SelectInput source="active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Active'},
                             {id: false, name: 'Inactive'},
                         ]}
                         options={{
                             floatingLabelText: '',
                         }}
            />

            <span style={styles.fieldLabel}>
                Lock this Sysuser Account
                <TooltipIcon dataFor="ttLocked"/>
                <ReactTooltip id="ttLocked" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>Locked</em>, this Sysuser account cannot access the Admin.
                </ReactTooltip>
            </span>
            <SelectInput source="locked"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Account is Locked'},
                             {id: false, name: 'Account is not Locked'},
                         ]}
                         options={{
                             floatingLabelText: '',
                         }}
            />

            <hr style={{...styles.break, ...styles.paddingBottom}}/>

            <span style={styles.fieldLabel}>
                Change Sysuser Password (if desired):
                <TooltipIcon dataFor="ttPass"/>
                <ReactTooltip id="ttPass" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Modify these fields only if you wish to save a new password.
                </ReactTooltip>
            </span>
            <TextInput source="sys_new_password" label="Change Password" type="password"
                       validate={validateChangePassword}/>
            <TextInput source="sys_confirm_new_password" label="Confirm Change Password" type="password"
                       validate={validateChangePassword}/>

            <span style={styles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Sysusers List.
            </span>
            <hr style={{...styles.break, ...styles.paddingBottom}}/>
        </SimpleForm>
    </Edit>
);



// WEBPACK FOOTER //
// ./src/Fuse/System/sysUser.js